import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExecutionProgress from "../../../../components/Dashboards/Executions/ExecutionProgress";
import ExecutionStatusChip from "../../../../components/Dashboards/Executions/ExecutionStatusChip";
import {
  formatExecutionDateTime,
  toApiDate,
} from "../../../../components/Dashboards/Executions/utils";
import { IDashboardGenerateExecution } from "../../../../models/dashboard-generate-execution";
import { dashboardGenerateExecutionService } from "../../../../services/dashboardGenerateExecution.service";
import ExecutionsFilter, { IExecutionsFilterParams } from "./ExecutionsFilter";

const translationPath = "page.dashboardExecutions.";

const ExecutionsPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const navigate = useNavigate();

  const [refresh, setRefresh] = useState(0);

  const [filterParams, setFilterParams] = useState<IExecutionsFilterParams>({
    Code: "",
    DashboardPanel: null,
    StartDate: null,
    EndDate: null,
    Status: "",
  });

  const openDetail = useCallback((id: string) => navigate(id), [navigate]);

  const columns: GridColDef<IDashboardGenerateExecution>[] = useMemo(
    () => [
      {
        field: "code",
        headerName: t(translationPath + "code") || "code",
        maxWidth: 90,
      },
      {
        field: "dashboardPanel",
        headerName: t(translationPath + "panel") || "panel",
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const name = params.row.dashboardPanel?.name ?? "-";
          return (
            <OverflowTooltip
              text={name}
              tooltip={name}
              textTransform="none"
              variant="body2"
            />
          );
        },
      },
      {
        field: "originGenerateDashboard",
        headerName: t(translationPath + "origin") || "origin",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => params.row.originGenerateDashboard?.name ?? "-",
      },
      {
        field: "status",
        headerName: t(translationPath + "status_label") || "status_label",
        flex: 0.7,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => <ExecutionStatusChip status={params.value} />,
      },
      {
        field: "progress",
        headerName: t(translationPath + "progress") || "progress",
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => <ExecutionProgress execution={params.row} />,
      },
      {
        field: "failedGenerates",
        headerName:
          t(translationPath + "failed_generates") || "failed_generates",
        maxWidth: 110,
        sortable: false,
      },
      {
        field: "created",
        headerName: t(translationPath + "created") || "created",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => formatExecutionDateTime(params.value, language),
      },
      {
        field: "finishedDate",
        headerName: t(translationPath + "finished_date") || "finished_date",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => formatExecutionDateTime(params.value, language),
      },
    ],
    [t, language],
  );

  const handleFilterChange = useCallback(
    (
      newFilters:
        | IExecutionsFilterParams
        | ((prev: IExecutionsFilterParams) => IExecutionsFilterParams),
    ) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  return (
    <Box
      sx={{
        height: "100%",
        "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
          display: "none !important",
        },
      }}
    >
      <Table<IDashboardGenerateExecution>
        title={t(translationPath + "title")}
        columns={columns}
        service={dashboardGenerateExecutionService}
        searchInputPlaceHolder={t(translationPath + "search_placeholder")}
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        hideLog
        getRowId={(row) => row.id}
        onRowClick={(params) => openDetail(String(params.id))}
        rowActions={(params) => [
          {
            action: () => openDetail(params.row.id),
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details"),
          },
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            Code: filterParams.Code.trim() || undefined,
            DashboardPanelId: filterParams.DashboardPanel?.id,
            Status: filterParams.Status || undefined,
            StartDate: toApiDate(filterParams.StartDate),
            EndDate: toApiDate(filterParams.EndDate),
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <ExecutionsFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </Box>
  );
};

export default ExecutionsPage;
